import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardTitle } from '@/components/ui/card';
import ContextInput from '@/components/page-components/project/new-agent-form/ContextInput';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import BusinessHours from '@/components/page-components/project/agents/BusinessHours';
import type { BusinessHour } from '@/services/api';
import { useTranslation } from 'react-i18next';
import { Agent } from '@/types/Agents';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/redux/store';
import { createAgent } from '@/redux/agentsSlice';
import FormRow from '@/components/common/FormRow';
import { navigateToAgent } from '@/utils/navigationUtils';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  getPhoneLengthMessage,
  isValidNotificationLocalNumber,
} from '@/utils/phoneUtils';
import {
  isBusinessHourRangeValid,
  mergeBusinessHours,
} from '@/utils/availability';
import SummaryDigestField, {
  DEFAULT_DIGEST_HOUR,
} from '@/components/page-components/project/agents/ui/SummaryDigestField';
import BusinessInfoFields from '@/components/page-components/project/agents/ui/BusinessInfoFields';
import { NewAgentStepTimeline } from '@/components/page-components/project/agents/new-agent-flow/NewAgentStepTimeline';
import ServicesSetupStep, {
  type SetupServiceItem,
} from '@/components/page-components/project/agents/new-agent-flow/ServicesSetupStep';
import { Checkbox } from '@/components/ui/checkbox';
import { buildAgentDraftFromOnboarding } from '@/utils/agentOnboardingPrompt';
import {
  EMPTY_BUSINESS_INFO_FORM,
  formValueToBusinessInfo,
  formatBusinessInfoForPrompt,
  isValidBusinessEmail,
  isValidBusinessWebsite,
} from '@/utils/businessInfo';

// `value` is what gets written into the agent's prompt context and must stay
// English and stable -- it is read by the model, not the user. Only the label
// is translated. Previously the same string served as both, so the dropdowns
// could not be localised without changing what the agent is told.
const PERSONALITY_OPTIONS = [
  {
    value: 'Friendly',
    labelKey: 'agent.new.guided.personality.tones.friendly.translation',
    fallback: 'Friendly',
  },
  {
    value: 'Professional',
    labelKey: 'agent.new.guided.personality.tones.professional.translation',
    fallback: 'Professional',
  },
  {
    value: 'Formal',
    labelKey: 'agent.new.guided.personality.tones.formal.translation',
    fallback: 'Formal',
  },
  {
    value: 'Energetic',
    labelKey: 'agent.new.guided.personality.tones.energetic.translation',
    fallback: 'Energetic',
  },
  {
    value: 'Calm',
    labelKey: 'agent.new.guided.personality.tones.calm.translation',
    fallback: 'Calm',
  },
  {
    value: 'Premium',
    labelKey: 'agent.new.guided.personality.tones.premium.translation',
    fallback: 'Premium',
  },
] as const;

const LANGUAGE_OPTIONS = [
  {
    value: 'Latvian',
    labelKey: 'common.languages.lv.translation',
    fallback: 'Latvian',
  },
  {
    value: 'English',
    labelKey: 'common.languages.en.translation',
    fallback: 'English',
  },
] as const;

const FLOW_STEPS = [
  'business_profile',
  'working_hours',
  'services',
  'personality',
  'review',
] as const;

interface NewAgentProps {
  onAgentCreated?: (agent: Agent) => void;
}

const NewAgent: React.FC<NewAgentProps> = ({ onAgentCreated }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const businessNameRef = useRef<HTMLInputElement>(null);
  const hasEditedAdvancedContextRef = useRef(false);

  const [stepIndex, setStepIndex] = useState(0);
  const [maxReachedStepIndex, setMaxReachedStepIndex] = useState(0);
  const [businessName, setBusinessName] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [businessInfoForm, setBusinessInfoForm] = useState(() => ({
    ...EMPTY_BUSINESS_INFO_FORM,
  }));
  const [businessHours, setBusinessHours] = useState<BusinessHour[]>(() =>
    mergeBusinessHours(0, [])
  );
  const [setupServices, setSetupServices] = useState<SetupServiceItem[]>([]);
  const [personalityTone, setPersonalityTone] = useState<string>(
    PERSONALITY_OPTIONS[0].value
  );
  const [primaryLanguage, setPrimaryLanguage] = useState<string>(
    LANGUAGE_OPTIONS[0].value
  );
  const [additionalLanguages, setAdditionalLanguages] = useState<string[]>([]);
  const [advancedContext, setAdvancedContext] = useState('');
  const [showAdvancedContext, setShowAdvancedContext] = useState(false);
  const [digestEnabled, setDigestEnabled] = useState(true);
  const [digestHour, setDigestHour] = useState(DEFAULT_DIGEST_HOUR);
  const [createAgentError, setCreateAgentError] = useState<string | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [stepDirection, setStepDirection] = useState<'forward' | 'backward'>(
    'forward'
  );

  useEffect(() => {
    if (!isMobile && stepIndex === 0 && businessNameRef.current) {
      businessNameRef.current.focus();
    }
  }, [isMobile, stepIndex]);

  const businessInfo = useMemo(
    () =>
      formValueToBusinessInfo(businessInfoForm, {
        businessName,
        about: businessDescription,
      }),
    [businessInfoForm, businessName, businessDescription]
  );

  const generatedDraft = useMemo(
    () =>
      buildAgentDraftFromOnboarding({
        profile: {
          businessName,
        },
        personality: {
          tone: personalityTone,
          primaryLanguage,
          additionalLanguages,
        },
      }),
    [businessName, personalityTone, primaryLanguage, additionalLanguages]
  );

  useEffect(() => {
    if (!hasEditedAdvancedContextRef.current) {
      setAdvancedContext(generatedDraft.context);
    }
  }, [generatedDraft.context]);

  const translatePhoneLengthError = (countryCode: string) => {
    const message = getPhoneLengthMessage(countryCode);
    return t(message.key, {
      defaultValue: message.defaultValue,
      ...message.values,
    });
  };

  const validateCurrentStep = () => {
    if (FLOW_STEPS[stepIndex] === 'business_profile') {
      if (!businessName.trim()) {
        return t(
          'agent.new.guided.validation.business_name.translation',
          'Business name is required.'
        );
      }
      if (!isValidBusinessWebsite(businessInfoForm.website)) {
        return t(
          'agent.new.guided.validation.business_website.translation',
          'Please enter a valid website.'
        );
      }
      if (!isValidBusinessEmail(businessInfoForm.publicEmail)) {
        return t(
          'agent.new.guided.validation.business_email.translation',
          'Please enter a valid email address.'
        );
      }
      if (
        businessInfoForm.publicPhoneLocalNumber &&
        !isValidNotificationLocalNumber(
          businessInfoForm.publicPhoneLocalNumber,
          businessInfoForm.publicPhoneCountryCode
        )
      ) {
        return translatePhoneLengthError(
          businessInfoForm.publicPhoneCountryCode
        );
      }
    }

    return null;
  };

  const handleNext = () => {
    const error = validateCurrentStep();
    if (error) {
      setStepError(error);
      return;
    }
    setStepError(null);
    setStepDirection('forward');
    const nextIndex = Math.min(stepIndex + 1, FLOW_STEPS.length - 1);
    setMaxReachedStepIndex(previous => Math.max(previous, nextIndex));
    setStepIndex(nextIndex);
  };

  const handleBack = () => {
    setStepError(null);
    setStepDirection('backward');
    setStepIndex(previous => Math.max(previous - 1, 0));
  };

  const handleGoToStep = (targetIndex: number) => {
    if (
      isCreating ||
      targetIndex < 0 ||
      targetIndex >= FLOW_STEPS.length ||
      targetIndex > maxReachedStepIndex ||
      targetIndex === stepIndex
    ) {
      return;
    }
    setStepError(null);
    setStepDirection(targetIndex < stepIndex ? 'backward' : 'forward');
    setStepIndex(targetIndex);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (stepIndex < FLOW_STEPS.length - 1) {
      handleNext();
    }
  };

  const handleCreateAgent = async () => {
    setCreateAgentError(null);
    const hoursToSave = mergeBusinessHours(0, businessHours);
    const invalidHour = hoursToSave.find(
      h => !h.is_closed && !isBusinessHourRangeValid(h.start_time, h.end_time)
    );
    if (invalidHour) {
      setCreateAgentError(
        t(
          'agent.business_hours.invalid_range.translation',
          'Closing time must be after opening time for each open day.'
        )
      );
      return;
    }

    setIsCreating(true);

    try {
      const result = await dispatch(
        createAgent({
          name: generatedDraft.name,
          context: advancedContext,
          description: generatedDraft.description,
          config: {
            notifications: {
              summary_email_delivery: digestEnabled ? 'digest' : 'immediate',
              summary_digest_hour: digestHour,
            },
          },
          business_info: businessInfo,
          business_hours: hoursToSave.map(h => ({
            day_of_week: h.day_of_week,
            start_time: h.start_time,
            end_time: h.end_time,
            is_closed: h.is_closed,
          })),
          services: setupServices.map(service => ({
            name: service.name,
            description: service.description || null,
            duration_minutes: service.durationMinutes,
            price: service.price,
            enabled: service.enabled,
          })),
          created_at: new Date().toISOString(),
        })
      ).unwrap();

      if (onAgentCreated) {
        onAgentCreated(result.agent);
      }
      if (result.agent.id !== undefined) {
        navigateToAgent(navigate, result.agent.id);
      }
    } catch (error) {
      if (error instanceof Error) {
        setCreateAgentError(error.message);
      } else {
        setCreateAgentError(t('agent.new.errors.unknown.translation'));
      }
    } finally {
      setIsCreating(false);
    }
  };

  const renderStep = () => {
    switch (FLOW_STEPS[stepIndex]) {
      case 'business_profile':
        return (
          <>
            <div className="space-y-1 px-4 sm:px-6 py-2">
              <CardTitle>
                {t(
                  'agent.new.guided.profile.title.translation',
                  'Business Profile'
                )}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {t(
                  'agent.new.guided.profile.description.translation',
                  'Add the facts callers usually ask for. Everything except business name is optional.'
                )}
              </p>
            </div>
            <FormRow
              required
              label={t(
                'agent.new.guided.profile.business_name.translation',
                'Business Name'
              )}
            >
              <Input
                ref={businessNameRef}
                required
                value={businessName}
                onChange={event => setBusinessName(event.target.value)}
                className="bg-card"
                placeholder={t(
                  'agent.new.guided.profile.business_name_placeholder.translation',
                  'e.g. Green Barber Riga'
                )}
              />
            </FormRow>
            <FormRow
              label={t(
                'agent.new.guided.profile.short_description.translation',
                'Short Description'
              )}
            >
              <Textarea
                value={businessDescription}
                onChange={event => setBusinessDescription(event.target.value)}
                className="bg-card"
                autogrow
                placeholder={t(
                  'agent.new.guided.profile.short_description_placeholder.translation',
                  'A short intro about your business'
                )}
              />
            </FormRow>
            <BusinessInfoFields
              value={businessInfoForm}
              onChange={setBusinessInfoForm}
              showBusinessName={false}
            />
            <FormRow
              label={t(
                'agent.settings.notifications.title.translation',
                'Call Summary Emails'
              )}
            >
              <SummaryDigestField
                enabled={digestEnabled}
                hour={digestHour}
                onEnabledChange={setDigestEnabled}
                onHourChange={setDigestHour}
              />
            </FormRow>
          </>
        );
      case 'working_hours':
        return (
          <div className="px-2 py-2 sm:px-4">
            <BusinessHours
              compact
              value={businessHours}
              onChange={setBusinessHours}
            />
          </div>
        );
      case 'services':
        return (
          <div className="px-4 py-2 sm:px-6">
            <ServicesSetupStep
              services={setupServices}
              onChange={setSetupServices}
            />
          </div>
        );
      case 'personality':
        return (
          <div className="space-y-4 px-4 sm:px-6 py-2">
            <CardTitle>
              {t(
                'agent.new.guided.personality.title.translation',
                'Personality & Language'
              )}
            </CardTitle>
            <FormRow
              label={t('agent.new.guided.personality.tone.translation', 'Tone')}
            >
              <Select
                value={personalityTone}
                onValueChange={setPersonalityTone}
              >
                <SelectTrigger className="bg-card">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERSONALITY_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.labelKey, option.fallback)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormRow>
            <FormRow
              label={t(
                'agent.new.guided.personality.primary_language.translation',
                'Primary Language'
              )}
            >
              <Select
                value={primaryLanguage}
                onValueChange={setPrimaryLanguage}
              >
                <SelectTrigger className="bg-card">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.labelKey, option.fallback)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormRow>
            <FormRow
              label={t(
                'agent.new.guided.personality.additional_languages.translation',
                'Additional Languages'
              )}
            >
              <div className="space-y-2">
                {LANGUAGE_OPTIONS.filter(
                  language => language.value !== primaryLanguage
                ).map(language => (
                  <label
                    key={language.value}
                    className="flex items-center gap-2 rounded-md border border-border bg-card p-3 text-sm"
                  >
                    <Checkbox
                      checked={additionalLanguages.includes(language.value)}
                      onCheckedChange={checked => {
                        if (checked === true) {
                          setAdditionalLanguages(previous => [
                            ...previous,
                            language.value,
                          ]);
                          return;
                        }
                        setAdditionalLanguages(previous =>
                          previous.filter(
                            existingLanguage =>
                              existingLanguage !== language.value
                          )
                        );
                      }}
                    />
                    <span>{t(language.labelKey, language.fallback)}</span>
                  </label>
                ))}
              </div>
            </FormRow>
          </div>
        );
      case 'review':
        return (
          <div className="space-y-4 px-4 sm:px-6 py-2">
            <CardTitle>
              {t(
                'agent.new.guided.review.title.translation',
                'Review & Create'
              )}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {t(
                'agent.new.guided.review.description.translation',
                'We generated your agent configuration from the answers above.'
              )}
            </p>
            <div className="space-y-2 rounded-lg border border-border bg-card p-4">
              <p className="text-sm font-medium text-foreground">
                {t('agent.new.name.translation', 'Name')}
              </p>
              <p className="text-sm text-muted-foreground">
                {generatedDraft.name}
              </p>
            </div>
            <div className="space-y-2 rounded-lg border border-border bg-card p-4">
              <p className="text-sm font-medium text-foreground">
                {t('agent.new.description.translation', 'Description')}
              </p>
              <p className="text-sm text-muted-foreground">
                {generatedDraft.description}
              </p>
            </div>
            {formatBusinessInfoForPrompt(businessInfo) && (
              <div className="space-y-2 rounded-lg border border-border bg-card p-4">
                <p className="text-sm font-medium text-foreground">
                  {t(
                    'agent.new.guided.review.business_info.translation',
                    'Business information'
                  )}
                </p>
                <p className="whitespace-pre-line text-sm text-muted-foreground">
                  {formatBusinessInfoForPrompt(businessInfo)}
                </p>
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAdvancedContext(previous => !previous)}
            >
              {showAdvancedContext
                ? t(
                    'agent.new.guided.review.hide_advanced.translation',
                    'Hide advanced prompt'
                  )
                : t(
                    'agent.new.guided.review.show_advanced.translation',
                    'Show advanced prompt'
                  )}
            </Button>
            {showAdvancedContext && (
              <FormRow label={t('agent.new.context.translation', 'Context')}>
                <ContextInput
                  value={advancedContext}
                  onChange={(val: string) => {
                    hasEditedAdvancedContextRef.current = true;
                    setAdvancedContext(val);
                  }}
                />
              </FormRow>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col w-full max-w-4xl mx-auto">
      <div className="flex flex-col gap-3 sm:gap-4">
        <Card className="w-full bg-card p-4 gap-4 space-y-4 sm:space-y-6 sm:p-6">
          <form
            onSubmit={handleFormSubmit}
            className="flex flex-col gap-3 sm:gap-4"
          >
            <div className="flex items-center justify-between mb-2 gap-2">
              <CardTitle>
                {t('agent.new.guided.title.translation', 'Create New Agent')}
              </CardTitle>
            </div>
            <NewAgentStepTimeline
              steps={FLOW_STEPS}
              currentIndex={stepIndex}
              maxReachableIndex={maxReachedStepIndex}
              onStepClick={handleGoToStep}
              disabled={isCreating}
            />
            <div
              key={stepIndex}
              className={`motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-300 ${
                stepDirection === 'forward'
                  ? 'motion-safe:slide-in-from-right-2'
                  : 'motion-safe:slide-in-from-left-2'
              }`}
            >
              {renderStep()}
            </div>
            {stepError && (
              <div className="px-4 sm:px-6 text-sm text-destructive">
                {stepError}
              </div>
            )}
            {createAgentError && (
              <div className="px-4 sm:px-6 text-sm text-red-500 dark:text-red-400">
                {createAgentError}
              </div>
            )}
            <div className="flex flex-row items-center justify-between gap-2 pt-2 px-4 sm:px-6">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={handleBack}
                disabled={stepIndex === 0 || isCreating}
                className="min-w-0 flex-1 justify-center sm:flex-none sm:w-auto"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                {t('common.back.translation', 'Back')}
              </Button>

              {stepIndex === FLOW_STEPS.length - 1 ? (
                <Button
                  variant="gradient"
                  type="button"
                  size="lg"
                  disabled={isCreating}
                  onClick={handleCreateAgent}
                  className="min-w-0 flex-1 justify-center sm:flex-none sm:w-auto"
                >
                  {isCreating
                    ? t('agent.new.guided.creating.translation', 'Creating...')
                    : t('agent.new.guided.create.translation', 'Create Agent')}
                  <ChevronRightIcon className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="gradient"
                  size="lg"
                  onClick={handleNext}
                  className="min-w-0 flex-1 justify-center sm:flex-none sm:w-auto"
                >
                  {t('common.next.translation', 'Next')}
                  <ChevronRightIcon className="w-4 h-4" />
                </Button>
              )}
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default NewAgent;
